import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useCallback, useEffect } from 'react'
import PaymentSuccessModal from '../../../components/modals/PaymentSuccessModal'
import { useAuth } from '../../../contexts/AuthContext'
import { useClientProfile } from '../../../hooks/useClientProfile'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../../services/query/queryKeys'
import { AppLayout } from '../../../components/layouts/AppLayout'
import {
  clearPendingCreditPurchase,
  getPendingCreditPurchase,
  PAYMENT_CREDITS_BEFORE_KEY,
  PAYMENT_RETURN_URL_KEY,
} from '../../../utils/paymentCheckout'

export const Route = createFileRoute('/billing/success')({
  component: PaymentSuccessPage,
})

function PaymentSuccessPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentUser } = useAuth()
  const clientQuery = useClientProfile(currentUser?.email)
  const urlParams = new URLSearchParams(window.location.search)
  const sessionId = urlParams.get('session_id')
  const pendingPurchase = getPendingCreditPurchase()
  const hasVerifiedReturnContext = Boolean(sessionId && pendingPurchase)

  // Refresh credits data after successful payment
  useEffect(() => {
    if (currentUser?.email && hasVerifiedReturnContext) {
      void clientQuery.refetch().then((result) => {
        if (typeof result.data?.credits === 'number') {
          queryClient.setQueryData(queryKeys.profile.client(currentUser.email), (existing: any) => ({
            ...existing,
            ...result.data,
            credits: result.data.credits,
          }))
        }
      })
    } else if (!hasVerifiedReturnContext) {
      const returnPath = sessionStorage.getItem(PAYMENT_RETURN_URL_KEY)
      clearPendingCreditPurchase()
      sessionStorage.removeItem(PAYMENT_CREDITS_BEFORE_KEY)
      sessionStorage.removeItem(PAYMENT_RETURN_URL_KEY)

      navigate({ to: (returnPath && returnPath !== '/' ? returnPath : '/campaigns') as any, replace: true })
    }
  }, [clientQuery, currentUser?.email, hasVerifiedReturnContext, navigate, queryClient])

  const handleClose = useCallback(() => {
    sessionStorage.removeItem(PAYMENT_CREDITS_BEFORE_KEY)
    // Return to the original page where user was before payment
    const returnPath = sessionStorage.getItem(PAYMENT_RETURN_URL_KEY)
    sessionStorage.removeItem(PAYMENT_RETURN_URL_KEY)
    clearPendingCreditPurchase()

    if (currentUser?.email && typeof clientQuery.data?.credits === 'number') {
      queryClient.setQueryData(queryKeys.profile.client(currentUser.email), (existing: any) => ({
        ...existing,
        ...clientQuery.data,
        credits: clientQuery.data.credits,
      }))
    }

    if (returnPath && returnPath !== '/') {
      navigate({ to: returnPath as any })
    } else {
      navigate({ to: '/campaigns' })
    }
  }, [clientQuery.data, currentUser?.email, navigate, queryClient])

  return (
    <AppLayout
      currentView="campaigns"
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
    >
      <PaymentSuccessModal
        isOpen={hasVerifiedReturnContext}
        onClose={() => {
          handleClose()
        }}
      />
    </AppLayout>
  )
}
